import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LuX } from 'react-icons/lu';

/**
 * The module's dialogue — behind "الميزات" and "السياسات" on a subscription
 * card, the course page's materials and video, and the checkout.
 *
 * The design does not draw one — those two words are links on the card and the
 * file stops there — so this is the module's own, built from the card's
 * language: the same white, the same hairline, the same radius and shadow.
 *
 * A component of its own rather than semantic-ui's `Modal`, which the old page
 * uses: the styling rules keep that library out of `src/modules`, and its
 * dimmer fights `V2Root`'s `dir` because it mounts outside it.
 *
 * IN A PORTAL, AND WHY THE `dir` COMES ALONG
 *
 * It renders into <body> so no ancestor's `overflow` or `transform` can clip or
 * re-anchor it. That puts it outside `V2Root`, which is where `dir` and the
 * module's font live — so both are set again here. Without them a dialogue over
 * an Arabic page comes up left-to-right in the app's global Cairo.
 *
 * The page behind is locked while it is open. Restoring the previous value
 * rather than clearing it matters: something else may already have locked the
 * body — the old profile modal at sign-in does — and clearing would unlock it.
 *
 * THREE WIDTHS, ONE FOOTER
 *
 * `md` is the reading width the two text dialogues were drawn at. `wide` is the
 * course page's video player, which needs the room; `narrow` is the checkout,
 * a single column of fields that a reading width would stretch thin.
 *
 * `footer` is pinned under the scrolling body, so an action that closes the
 * dialogue's business — the checkout's button — stays in reach however long
 * the body above it runs.
 */
const WIDTHS = {
    narrow: 'tw-max-w-[480px]',
    md: 'tw-max-w-[720px]',
    wide: 'tw-max-w-[1100px]',
};

const Modal = ({ isOpen, title, onClose, size = 'md', footer, children }) => {
    const { t, i18n } = useTranslation();
    const closeRef = useRef(null);

    // Read through a ref so a caller passing a new function on every render
    // does not re-run the effect below — which would pull focus back to the
    // close button out of whatever field the reader is typing in.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const isArabic = i18n.language?.startsWith('ar');

    useEffect(() => {
        if (!isOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const onKeyDown = (event) => { if (event.key === 'Escape') onCloseRef.current(); };
        document.addEventListener('keydown', onKeyDown);

        // The dialogue opens with focus inside it, so the next Tab walks its
        // own controls instead of the page still sitting behind the backdrop.
        closeRef.current?.focus();

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return createPortal(
        <div
            dir={isArabic ? 'rtl' : 'ltr'}
            lang={isArabic ? 'ar' : 'en'}
            className="v2-root tw-font-v2 !tw-bg-transparent tw-fixed tw-inset-0 tw-z-[1000] tw-flex tw-items-center tw-justify-center tw-p-[16px]"
        >
            {/* The backdrop is its own element behind the panel, not a border on
                it: a click has to be able to land on the backdrop and nowhere
                else for "click outside to close" to mean anything. */}
            <div
                className="tw-absolute tw-inset-0 tw-bg-black/40"
                onClick={onClose}
                aria-hidden
            />

            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className={
                    `tw-relative tw-flex tw-max-h-[85vh] tw-w-full ${WIDTHS[size] ?? WIDTHS.md} tw-flex-col ` +
                    'tw-overflow-hidden tw-rounded-[15.325px] tw-bg-white tw-shadow-v2-card ' +
                    'tw-border tw-border-solid tw-border-v2-neutral-200'
                }
            >
                <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-4 tw-border-0 tw-border-b tw-border-solid tw-border-v2-neutral-200 tw-px-[24px] tw-py-[16px]">
                    <h2 className="tw-m-0 tw-text-[18px] tw-font-bold tw-leading-[1.5] tw-text-v2-student-ink">
                        {title}
                    </h2>

                    <button
                        ref={closeRef}
                        type="button"
                        onClick={onClose}
                        aria-label={t('v2.modal.close')}
                        className={
                            'tw-flex tw-size-[32px] tw-shrink-0 tw-items-center tw-justify-center ' +
                            'tw-rounded-full tw-text-v2-text-excerpt hover:tw-bg-v2-neutral-50 ' +
                            'tw-transition-colors focus-visible:tw-outline-none ' +
                            'focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand'
                        }
                    >
                        <LuX size={18} aria-hidden />
                    </button>
                </div>

                <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-[24px] tw-py-[20px]">
                    {children}
                </div>

                {footer && (
                    <div className="tw-shrink-0 tw-border-0 tw-border-t tw-border-solid tw-border-v2-neutral-200 tw-px-[24px] tw-py-[16px]">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default Modal;
